import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, IconButton, FormControlLabel, Typography } from '@mui/material';

import { FHSelect, FHRadioGroup, FHRadio } from '../../../../base';
import { GOAL_EVALUATION_ENUM } from '../constants';
import { FORTA_BLACK, FORTA_BLUE, FORTA_GRAY2 } from '../../../../../utils/colors';
import { parentSupervisionActions } from '../../../../../store';
import DeleteIcon from '../../../../../assets/images/trash-new.svg';
import { SupervisionNoteStatus } from '../../../../../constants';

import type {
  IParentGoalProgress,
  ISelectOption,
  SelectValueType,
} from '../../../../../utils/types';
import type { ChangeEvent } from 'react';

interface ParentGoalsRowProps {
  index: number;
  isEditable: boolean;
  goalProgress: IParentGoalProgress;
  goalOptions: Array<ISelectOption>;
  addedGoals: Array<number>;
  onChange: (prevId: number, id: number) => void;
  onDelete: (progress: IParentGoalProgress) => void;
}

function ParentGoalsRow({
  index,
  isEditable,
  goalOptions,
  goalProgress,
  addedGoals,
  onChange,
  onDelete,
}: ParentGoalsRowProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current, loading: parentSNLoading } = useSelector(
    (state: any) => state.parentSupervision,
  );
  const isSupervisionCompleted = current?.status === SupervisionNoteStatus.Completed;

  const [rowInfo, setRowInfo] = useState<IParentGoalProgress>({
    goal_id: 0,
    goal_evaluation: '',
  });

  useEffect(() => {
    if (!goalProgress) return;

    setRowInfo(goalProgress);
  }, [goalProgress]);

  const goals = useMemo(() => {
    return goalOptions.filter(
      (option: ISelectOption) =>
        !addedGoals.includes(+option.value) || rowInfo.goal_id === option.value,
    );
  }, [goalOptions, addedGoals, rowInfo]);

  const handleFieldUpdate = (field: string, value: string | number) => {
    if (!field) return;
    const data = { ...rowInfo, [field]: value };
    setRowInfo(data);
    handleSaveGoal(data);
  };

  const handleSaveGoal = (data: IParentGoalProgress) => {
    if (!data.goal_id || !data.goal_evaluation || !current?.id) {
      return;
    }

    if (!goalProgress?.id) {
      dispatch(
        parentSupervisionActions.createParentSupervisionGoalProgress({
          data,
          getAccessTokenSilently,
          parentSupervisionId: current.id,
          index,
        }),
      );
    } else {
      dispatch(
        parentSupervisionActions.updateParentSupervisionGoalProgress({
          data,
          getAccessTokenSilently,
          parentSupervisionId: current.id,
          goalProgressId: goalProgress.id,
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        height: 75,
        display: 'flex',
        alignItems: 'center',
        py: 1,
        pl: 2,
        borderBottom: index === goalOptions.length - 1 ? 'none' : `1px solid ${FORTA_GRAY2}`,
      }}
    >
      {isEditable && (
        <IconButton
          sx={{
            p: 1,
            mr: 2,
          }}
          onClick={() => {
            onDelete(goalProgress);
          }}
        >
          <img src={DeleteIcon} alt="Delete" />
        </IconButton>
      )}
      <Box sx={{ flexGrow: 1, display: 'flex' }}>
        {isSupervisionCompleted ? (
          <Typography fontSize={16} padding={1} textAlign="left" color={FORTA_BLACK}>
            {goalOptions.find((option) => option.value === rowInfo.goal_id)?.label || ''}
          </Typography>
        ) : (
          <FHSelect
            data-testid="goalOptionsDropdown"
            sx={{
              flexGrow: 1,
              boxShadow: 'none',
              backgroundColor: FORTA_BLUE.light3,
              borderRadius: '10px',
            }}
            inputHeight={40}
            fullWidth
            disabled={!isEditable || parentSNLoading}
            options={goals}
            value={goalOptions.length > 0 && rowInfo ? rowInfo.goal_id : ''}
            onUpdate={(value: SelectValueType) => {
              const goalId = Number(value);
              if (!Number.isInteger(goalId)) return;

              if (goals.length === 1) {
                setRowInfo((prev) => ({ ...prev, goal_id: goalId }));
              } else {
                onChange(rowInfo.goal_id, goalId);
                handleFieldUpdate('goal_id', goalId);
              }
            }}
          />
        )}
      </Box>
      <FHRadioGroup
        row
        sx={{ justifyContent: 'space-between', mt: 2 }}
        value={rowInfo.goal_evaluation}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          handleFieldUpdate('goal_evaluation', e.target.value);
        }}
      >
        <Box sx={{ width: 210, justifyContent: 'center' }}>
          <FormControlLabel
            disabled={!isEditable || parentSNLoading}
            data-testid={GOAL_EVALUATION_ENUM.NEEDS_IMPROVEMENT}
            sx={{
              flexGrow: 1,
              textAlign: 'center',
            }}
            value={GOAL_EVALUATION_ENUM.NEEDS_IMPROVEMENT}
            control={
              <FHRadio
                sx={{ '&.MuiRadio-root': { boxShadow: 'none' } }}
                showDisabled={isEditable}
              />
            }
            label=""
          />
        </Box>
        <Box sx={{ width: 210, justifyContent: 'center', alignItems: 'center' }}>
          <FormControlLabel
            disabled={!isEditable || parentSNLoading}
            data-testid={GOAL_EVALUATION_ENUM.MEETS_EXPECTATIONS}
            sx={{
              flexGrow: 1,
              textAlign: 'center',
            }}
            value={GOAL_EVALUATION_ENUM.MEETS_EXPECTATIONS}
            control={
              <FHRadio
                sx={{ '&.MuiRadio-root': { boxShadow: 'none' } }}
                showDisabled={isEditable}
              />
            }
            label=""
          />
        </Box>
        <Box sx={{ width: 210, justifyContent: 'center' }}>
          <FormControlLabel
            disabled={!isEditable || parentSNLoading}
            data-testid={GOAL_EVALUATION_ENUM.EXCEEDS_EXPECTATIONS}
            sx={{
              flexGrow: 1,
              textAlign: 'center',
            }}
            value={GOAL_EVALUATION_ENUM.EXCEEDS_EXPECTATIONS}
            control={
              <FHRadio
                sx={{ '&.MuiRadio-root': { boxShadow: 'none' } }}
                showDisabled={isEditable}
              />
            }
            label=""
          />
        </Box>
      </FHRadioGroup>
    </Box>
  );
}

export default ParentGoalsRow;
